import { cn } from '@ui/lib';
import { type VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';

import { BaseButtonProps, buttonVariants } from './buttonConfig';
import { ButtonContent } from './ButtonContent';
import { IconProps } from '../icon/Icon';
import { omit } from 'lodash';

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;

export interface CommonButtonProps extends ButtonVariantProps, BaseButtonProps {
  variant?: Exclude<ButtonVariantProps['variant'], 'icon'>;
  name?: ButtonHTMLAttributes<Element>['name'];
  value?: ButtonHTMLAttributes<Element>['value'];
  disabled?: ButtonHTMLAttributes<Element>['disabled'];
  onClick?: ButtonHTMLAttributes<Element>['onClick'];
  type?: ButtonHTMLAttributes<Element>['type'];
  id?: string;
}

interface ButtonIconProps
  extends Omit<
    CommonButtonProps,
    'leadingIcon' | 'trailingIcon' | 'loadingText' | 'labelText' | 'variant'
  > {
  variant: 'icon';
  icon: Pick<IconProps, 'fileName' | 'size'>;
}

export type ButtonProps = CommonButtonProps | ButtonIconProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      format = 'full',
      outsidePadding = false,
      className,
      disabled,
      isLoading,
      sideBySide = false,
      sticky,
      ...props
    },
    ref,
  ) => {
    const leadingIcon =
      props.variant !== 'icon' ? props.leadingIcon : props.icon;
    const trailingIcon =
      props.variant !== 'icon' ? props.trailingIcon : undefined;
    const labelText = props.variant !== 'icon' ? props.labelText : '';
    const loadingText = props.variant !== 'icon' ? props.loadingText : '';

    const sanitizedProps = omit(props, [
      'labelText',
      'leadingIcon',
      'trailingIcon',
      'icon',
      'loadingText',
    ]);

    return (
      <button
        ref={ref}
        {...sanitizedProps}
        className={cn(
          buttonVariants({
            variant: props.variant ?? 'primary',
            format,
            outsidePadding,
            sticky,
          }),
          className,
        )}
        disabled={disabled}
        data-loading={isLoading ? 'true' : 'false'}
      >
        <ButtonContent
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          labelText={labelText ?? ''}
          loadingText={loadingText ?? ''}
          format={format ?? 'full'}
        />
      </button>
    );
  },
);
Button.displayName = 'Button';
